import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { listNotifications, markNotificationsRead } from '../api/notifications';

const NotificationContext = createContext(null);
const STORAGE_KEY = 'ft_notifications';
const MAX = 100;

const supportsBrowserNotif = typeof window !== 'undefined' && 'Notification' in window;

function showBrowserNotification(message) {
  if (!supportsBrowserNotif || Notification.permission !== 'granted') return;
  try {
    // Chèn giờ gửi (HH:MM) để biết thông báo phát lúc nào.
    const hhmm = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const n = new Notification('FunnyTask', {
      body: `${message} · ${hhmm}`,
      icon: '/icon-192.png',
      badge: '/badge-96.png',
      tag: 'ft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (e) {}
}

export function NotificationProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [permission, setPermission] = useState(supportsBrowserNotif ? Notification.permission : 'unsupported');
  const lastSyncRef = useRef(0);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX)));
    } catch (e) {}
  }, [items]);

  // Gộp NHIỀU thông báo vào danh sách theo KHÓA `id` (nid) -> tin nhận realtime và tin tải-bù từ server
  // KHÔNG bị tính thành 2. Giữ trạng thái đã-đọc nếu MỘT trong hai phía đã đọc. Sắp mới nhất trước.
  const upsertMany = useCallback((list) => {
    if (!list || !list.length) return;
    setItems((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      for (const n of list) {
        if (!n || !n.id) continue;
        const old = map.get(n.id);
        map.set(n.id, old ? { ...old, ...n, read: old.read || n.read } : n);
      }
      return Array.from(map.values()).sort((a, b) => (b.at || 0) - (a.at || 0)).slice(0, MAX);
    });
  }, []);

  // Xin quyền hiển thị thông báo trên trình duyệt (1 lần, TỪ thao tác người dùng — bắt buộc trên iOS).
  // Sau khi cấp quyền -> ĐĂNG KÝ Web Push (tạo subscription gửi về server) để nhận cả khi đóng app.
  const enableBrowserNotifications = useCallback(async () => {
    if (!supportsBrowserNotif) return 'unsupported';
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === 'granted') {
      showBrowserNotification('Đã bật thông báo trên trình duyệt.');
      import('../push').then((m) => m.registerPush()).catch(() => {});
    }
    return p;
  }, []);

  // Thêm 1 thông báo (từ socket 'ft:notify'). Khóa theo `nid` nếu có để khớp với bản tải-bù từ server.
  const addNotification = useCallback((message, type = 'info', opts = {}) => {
    if (!message) return;
    const id = opts.nid || (Date.now() + '_' + Math.random().toString(36).slice(2, 6));
    upsertMany([{ id, message, type, at: opts.at || Date.now(), read: false, convId: opts.convId || '' }]);
    // Thông báo OS do WEB PUSH (service worker) lo — không bắn new Notification in-app để tránh HIỆN 2 LẦN.
  }, [upsertMany]);

  // Nhận thông báo realtime từ server (giao việc, đề xuất, lương…) do ChatContext phát qua window event.
  useEffect(() => {
    const onNotify = (e) => {
      const d = e.detail || {};
      const msg = [d.title, d.body].filter(Boolean).join(' — ');
      if (msg) addNotification(msg, d.type || 'info', { nid: d.nid, convId: d.convId, at: d.at });
    };
    window.addEventListener('ft:notify', onNotify);
    return () => window.removeEventListener('ft:notify', onNotify);
  }, [addNotification]);

  // TẢI BÙ từ server: khi mở app, khi socket nối lại (ChatContext bắn 'ft:notify-sync'), và khi tab hiện lại.
  // Bịt lỗ "socket rớt lúc server bắn 'notify' -> chuông mất thông báo vĩnh viễn".
  const sync = useCallback(async () => {
    const now = Date.now();
    if (now - lastSyncRef.current < 1500) return; // gộp các lần gọi sát nhau
    lastSyncRef.current = now;
    try {
      const list = await listNotifications();
      upsertMany(
        (list || []).map((n) => ({
          id: n.id,
          message: [n.title, n.body].filter(Boolean).join(' — '),
          type: n.type || 'info',
          at: n.at || Date.now(),
          read: !!n.read,
          convId: n.convId || '',
        }))
      );
    } catch (e) {}
  }, [upsertMany]);

  useEffect(() => {
    sync(); // tải bù khi mở app
    const onSync = () => { if (!document.hidden) sync(); };
    window.addEventListener('ft:notify-sync', onSync); // ChatContext bắn khi socket (re)connect
    document.addEventListener('visibilitychange', onSync, { passive: true });
    window.addEventListener('focus', onSync, { passive: true });
    return () => {
      window.removeEventListener('ft:notify-sync', onSync);
      document.removeEventListener('visibilitychange', onSync);
      window.removeEventListener('focus', onSync);
    };
  }, [sync]);

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    markNotificationsRead('all').catch(() => {}); // lưu server -> F5/đổi máy vẫn giữ đã-đọc
  }, []);
  const clearAll = useCallback(() => setItems([]), []);
  const removeOne = useCallback((id) => setItems((prev) => prev.filter((n) => n.id !== id)), []);

  const unread = items.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{ items, addNotification, markAllRead, clearAll, removeOne, unread, permission, enableBrowserNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
